/* eslint-disable react/no-unused-prop-types */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Icons } from '../../DataDisplay/Icon/icons';
import Icon, { Color } from '../../DataDisplay/Icon/Icon';
import { convertBase64, getImageInfo } from '../../../utils/utils';
import styles from './InputFile.module.scss';
import Buttonstyles from '../Button/Button.module.scss';
import Box from '../../Layout/Box/Box';
import ErrorText from '../../Text/ErrorText/ErrorText';
import { useDatadogLog } from '../logging';

export type InputFileProps<T> = {
  id: string;
  idFileName?: string;
  type?: 'button' | 'submit' | 'reset';
  color?: 'primary' | 'secondary' | 'outlined' | 'lined' | 'none' | 'hover';
  size?: 'sm' | 'lg' | 'xlg' | 'jumbo';
  name: T;
  variant?: 'outlined' | 'standard' | 'plain' | 'background' | 'dropdown';
  textColor?: string;
  round?: boolean;
  textSize?: number;
  bold?: boolean;
  background?: string;
  borderColor?: string;
  width?: string;
  label: string;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  showLoadingIcon?: boolean;
  noShadow?: boolean;
  noPadding?: boolean;
  hasLink?: boolean;
  icon?: Icons;
  iconLocation?: 'left' | 'right' | 'start' | 'end' | 'center';
  iconSize?: 'xsm' | 'sm' | 'md' | 'xlg';
  iconWidth?: string;
  value?: string;
  valueFileName?: string;
  height?: string;
  placeholder?: string;
  maxLength?: number;
  maxSize?: number;
  validExtensions?: string[];
  accept?: string;
  error?: string | null;
  hideLabel?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  invalidFileFormatMsg?: string;
  fileSizeErrorMsg?: string;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  onChangeFileName?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurFileName?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onValidation?: (hasError: boolean, errorMessage: string) => void;
};

const InputFile = <T extends string>(props: InputFileProps<T>): React.JSX.Element => {
  const {
    id,
    idFileName,
    type = 'button',
    color = 'secondary',
    size = 'lg',
    textColor = 'auto',
    round = false,
    textSize,
    bold = true,
    label = 'Button',
    background,
    borderColor,
    width,
    isSubmitting,
    isDisabled = false,
    showLoadingIcon = true,
    noShadow = false,
    noPadding,
    hasLink,
    icon,
    iconLocation,
    iconSize = 'sm',
    iconWidth,
    placeholder,
    name,
    maxLength,
    maxSize = -1,
    validExtensions,
    accept,
    valueFileName,
    error = null,
    hideLabel = false,
    maxWidth,
    maxHeight,
    invalidFileFormatMsg = 'Invalid file type. Please try again.',
    fileSizeErrorMsg = `Invalid file size. Max ${maxWidth}px x ${maxHeight}px`,
    onBlur,
    onChange = () => {},
    onChangeFileName = () => {},
    onBlurFileName = () => {},
    onValidation = () => {},
  } = props;
  const [base64File, setBase64File] = useState('');
  const [fileName, setfileName] = useState(valueFileName || '');
  const log = useDatadogLog();

  const isValidFileUploaded = (file: File) => {
    if (file) {
      const fileExtension = file.type.split('/')[1];
      if (validExtensions) {
        const extentionOfFile = file.name.split('.').pop() || '';
        return validExtensions.includes(fileExtension) || validExtensions.includes(extentionOfFile.toLowerCase());
      }
      return true;
    }
    return false;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const getBase64File = async (isisValid: boolean, errorMessage: string) => {
      if (isisValid) {
        const base64 = (await convertBase64(event.target)) as string;
        setBase64File(base64);
        onValidation(true, '');
      } else {
        setBase64File('');
        onValidation(false, errorMessage);
      }
    };
    const processFile = async () => {
      let isValid = false;
      let errorMessageToSend = '';
      if (event.target.files !== null) {
        const fileUploaded = event.target.files[0];

        if (isValidFileUploaded(fileUploaded)) {
          setfileName(fileUploaded.name);
          if (maxSize !== -1) {
            const maxSizeInBytes = maxSize * 1024 * 1024;
            if (fileUploaded.size > maxSizeInBytes) {
              const fileInput = event.target;
              fileInput.value = '';
              setfileName(`Max ${maxSize}MB allowed. Please try again.`);
              errorMessageToSend = `Max ${maxSize}MB allowed. Please try again.`;
            } else {
              isValid = true;
            }
          } else {
            isValid = true;
          }
        } else {
          errorMessageToSend = invalidFileFormatMsg;
          setfileName(invalidFileFormatMsg);
        }

        if (maxHeight && maxWidth && isValid && fileUploaded.type !== 'image/svg+xml') {
          const fileDimentsionInfo = await getImageInfo(fileUploaded);
          if (fileDimentsionInfo.height > maxHeight || fileDimentsionInfo.width > maxWidth) {
            setfileName(fileSizeErrorMsg);
            errorMessageToSend = fileSizeErrorMsg;
            isValid = false;
          }
        }
      }

      log({
        message: `File uploaded`,
        payload: { name: id, value: fileName },
        actionType: 'Input',
      });
      await getBase64File(isValid, errorMessageToSend);
    };

    return processFile();
  };

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  const onClick = () => {
    if (hiddenFileInput.current !== null) {
      hiddenFileInput.current.files = null;
      hiddenFileInput.current.value = '';
      hiddenFileInput.current.click();
    }
  };

  const inputEl = useRef<HTMLInputElement>(null);
  const inputEl2 = useRef<HTMLInputElement>(null);

  const forceFireOnChange = useCallback(() => {
    /*

    Note: this is an established hack for causing React's onChange synthetic event to fire.
    https://github.com/facebook/react/issues/11488

    Typically, input's onChange function only fires from direct user input. In this case we are using react state
    to set the value of the input, so we need to fire onChange manually.

    We are doing this so that this component can generate a React.ChangeEvent for any onChange callback that is given to it.

    */

    interface ExtendedElement extends HTMLInputElement {
      _valueTracker: { setValue: (val: string) => void };
    }

    const currentElement = inputEl.current as ExtendedElement;
    const currentElementFileName = inputEl2.current as ExtendedElement;

    if (currentElement) {
      const lastValue = currentElement.value;

      currentElement.value = base64File;
      currentElement._valueTracker?.setValue(lastValue);
    }
    if (currentElementFileName) {
      const lastValue = currentElementFileName.value;
      currentElementFileName.value = fileName;
      currentElementFileName._valueTracker?.setValue(lastValue);
    }
    inputEl.current?.setAttribute('fileName', fileName);

    /* eslint-enable-line @typescript-eslint/no-unsafe-call */

    inputEl2.current?.dispatchEvent(new Event('change', { bubbles: true }));
    inputEl.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [base64File]);
  useEffect(() => {
    forceFireOnChange();
  }, [base64File]);
  return (
    <>
      <span className={styles.span}>
        <button
          id={id}
          type={type}
          className={[
            Buttonstyles.button,
            Buttonstyles[size],
            Buttonstyles[color],
            Buttonstyles[getShadowClass()],
            Buttonstyles[getBoldClass()],
            Buttonstyles[getRoundClass()],
            Buttonstyles[getDisabledClass()],
            Buttonstyles[getPaddingClass()],
            Buttonstyles[getLinkClass()],
          ].join(' ')}
          style={{
            width: `${width}`,
            background: `${background}`,
            borderColor: `${borderColor}`,
            fontSize: `${textSize}em`,
            color: `${textColor}`,
          }}
          onClick={onClick}
          data-custom="true"
        >
          <Box jc="center" ai="center" position="relative">
            {iconLocation === 'left' && getIcon('left')}
            {isSubmitting && showLoadingIcon && (
              <Icon icon="loader" height="37px" width="37px" color={getIconColor()} />
            )}
            {label}
            {iconLocation === 'right' && getIcon('right')}
            {iconLocation === 'start' && getIcon('start')}
            {iconLocation === 'end' && getIcon('end')}
            {iconLocation === 'center' && getIcon('center')}
          </Box>
        </button>

        {!hideLabel &&
          (!fileName ? (
            <div className={styles.fileNameContainer}>No file chosen</div>
          ) : (
            <div className={styles.fileNameContainer}>{fileName}</div>
          ))}
      </span>
      <Box width="100%">
        <ErrorText error={error} size="sm" />
      </Box>
      <input
        id={`InputfileShow-${name}`}
        name={`Show-${name}`}
        type="file"
        ref={hiddenFileInput}
        className={styles.hidden}
        placeholder={placeholder}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={handleChange}
        accept={accept}
      />
      <input
        id={`Inputfile-${name}`}
        name={name}
        type="text"
        className={styles.hidden}
        ref={inputEl}
        onBlur={onBlur}
        onChange={(e) => {
          onChange(e);
        }}
      />
      <input
        id={`InputfileName-${idFileName}`}
        name={idFileName}
        type="text"
        className={styles.hidden}
        ref={inputEl2}
        onBlur={onBlurFileName}
        onChange={(e) => {
          onChangeFileName(e);
        }}
      />
    </>
  );
  function getDisabledClass() {
    return isSubmitting || isDisabled ? 'disabled' : '';
  }

  function getShadowClass() {
    return noShadow ? 'noShadow' : '';
  }

  function getBoldClass() {
    return bold ? 'bold' : 'normal';
  }

  function getRoundClass() {
    return round ? 'round' : '';
  }

  function getIconColor() {
    return textColor === 'white' ? 'medium-light' : 'primary-dark';
  }

  function getPaddingClass() {
    return noPadding ? 'noPadding' : '';
  }

  function getLinkClass() {
    return hasLink ? 'link' : '';
  }

  function getIcon(position: 'left' | 'right' | 'start' | 'end' | 'center') {
    const colorMap: { [key: string]: Color } = {
      primary: 'primary-light',
      secondary: 'primary-dark',
      none: 'primary-dark',
    };

    return <Icon icon={icon} color={colorMap[color]} position={position} size={iconSize} width={iconWidth} />;
  }
};

export default InputFile;
